import type { EMobileNotificationStatus } from '@/shared/api/generated/types/EMobileNotificationStatus';

/** Status da notificação mobile conforme contrato da API (EMobileNotificationStatus) */
export enum ENotificationMobileStatus {
  Draft = 1,
  Scheduled = 2,
  Processing = 3,
  Completed = 4,
  Canceled = 5,
}

export type NotificationStatus = 'delivered' | 'scheduled' | 'draft' | 'cancelled' | 'processing';

export interface NotificationSender {
  readonly name: string;
  readonly initials: string;
}

/** Estatísticas de entrega de uma notificação enviada */
export interface NotificationDeliveryStats {
  readonly deliveredCount: number;
  readonly deliveredPercent: number;
  readonly notDeliveredCount: number;
  readonly openCount: number;
  readonly openPercent: number;
}

export interface NotificationItem {
  readonly id: string;
  readonly sentAt: string;
  readonly createdAt: string;
  readonly title: string;
  readonly preview: string;
  readonly classrooms: readonly string[];
  readonly studentCount: number;
  readonly sender: NotificationSender;
  readonly status: NotificationStatus;
  readonly template: string | null;
  readonly urlExternal?: string;
  /** Estatísticas de entrega — disponíveis após a notificação ser enviada */
  readonly stats?: NotificationDeliveryStats;
}

export interface NotificationFilters {
  readonly search: string;
  readonly statuses: readonly NotificationStatus[];
  readonly startDate: string;
  readonly endDate: string;
  /** ID do remetente selecionado (string numérica ou vazia) */
  readonly sentBy: string;
  /** Nome exibido do remetente — usado para restaurar a seleção no Autocomplete */
  readonly sentByName: string;
  /** ID da turma selecionada (string numérica ou vazia) */
  readonly classroomId: string;
  /** Nome exibido da turma — usado para restaurar a seleção no Autocomplete */
  readonly classroomName: string;
}

/** Turma disponível para seleção de público no wizard */
export interface NotificationClassroom {
  readonly id: string;
  readonly name: string;
  readonly institution: string;
  readonly studentCount: number;
}

/** Status de matrícula disponível para filtragem de público */
export interface NotificationEnrollmentStatus {
  readonly id: string;
  readonly label: string;
}

/** Estado da etapa 1 do wizard — seleção de público */
export interface NotificationAudienceState {
  readonly classroomIds: readonly string[];
  readonly enrollmentStatuses: readonly string[];
  /** Mapa de id → studentCount das turmas selecionadas, para cálculo de total sem depender da lista completa */
  readonly classroomStudentCounts: Readonly<Record<string, number>>;
  /** Mapa de id → nome exibível das turmas selecionadas, para exibição no resumo */
  readonly classroomNames: Readonly<Record<string, string>>;
}

/** Estado da etapa 2 do wizard — composição da mensagem */
export interface NotificationMessageState {
  readonly title: string;
  readonly body: string;
  readonly url: string;
}

/** Modo de envio da notificação */
export type NotificationSendMode = 'now' | 'scheduled';

/** Estado do agendamento — etapa 3 do wizard */
export interface NotificationScheduleState {
  readonly mode: NotificationSendMode;
  /** ISO string da data/hora agendada; null quando mode = 'now' */
  readonly scheduledAt: string | null;
}

/** Estado completo do wizard de envio de notificação */
export interface NotificationWizardState {
  /** Índice da etapa atual (0 = Público, 1 = Mensagem, 2 = Revisão e envio) */
  readonly step: number;
  /** ID da notificação em edição; undefined quando criando uma nova */
  readonly notificationId?: number;
  readonly audience: NotificationAudienceState;
  readonly message: NotificationMessageState;
  readonly schedule: NotificationScheduleState;
  notificationType?: EMobileNotificationStatus | undefined;
}
